/**
 * Слияние истории из checkpoint с тем, что уже на экране (#1417).
 *
 * Checkpoint хранит ответ агента КУСКАМИ — каждая реплика между вызовами
 * инструментов отдельным сообщением, — а живой чат склеивает эти же куски в
 * одно сообщение через перевод строки. Сравнение по точному тексту их не
 * узнавало: оба куска считались «новыми» и дописывались в НАЧАЛО, над
 * вопросом пользователя (замер: 2 сообщения на экране + 3 из checkpoint → 4).
 *
 * Как сопоставляем (ревью !1296, два прохода):
 * - Участвуют только сообщения, которые checkpoint вообще хранит: реплики
 *   user/assistant с текстом и без виджета. «Conversation ended», карточки
 *   виджета, реплики команды, пустые — остаются на экране на своих местах и
 *   сопоставлению не мешают (раньше одно такое сообщение посреди экрана
 *   ломало всё и дублировало разговор).
 * - Реплика пользователя совпадает только ЦЕЛИКОМ («No» ≠ «Not sure»). Ответ
 *   агента собирается из идущих подряд кусков. Последний ответ на экране может
 *   быть ещё недопечатан — тогда он совпадает с началом куска.
 * - Экран — хвост разговора. Из всех мест checkpoint, с которых экран
 *   сходится, берём то, где сошлось БОЛЬШЕ всего сообщений; при равенстве —
 *   более позднее (повторённое «yes» — это последнее из них). Иначе
 *   разговор, у которого начало экрана равно концу checkpoint, дублировался.
 * - Всё, что в checkpoint РАНЬШЕ этого места, идёт перед экраном; всё, что
 *   ПОСЛЕ (ответ агента, сохранённый после ухода со страницы), — после.
 * - Экран и checkpoint не делят ни одной реплики — checkpoint целиком старше,
 *   он идёт перед экраном. Делят, но не выстраиваются — ничего не угадываем и
 *   показываем экран как есть: лучше не догрузить историю, чем задвоить её.
 */

const normalize = (text) => String(text ?? '').replace(/\s+/g, ' ').trim();

const ALIGNABLE_ROLES = new Set(['user', 'assistant']);

const isAlignable = (message) =>
  ALIGNABLE_ROLES.has(message?.role) && !message.widget && normalize(message.text) !== '';

/**
 * Собирается ли сообщение экрана из кусков checkpoint, начиная с `start`.
 * Возвращает индекс первого не съеденного куска или -1.
 */
const consumeMessage = (message, chunks, start, isLastOnScreen) => {
  const text = normalize(message.text);
  const chunk = chunks[start];
  if (!chunk || chunk.role !== message.role) return -1;

  if (message.role === 'user') return chunk.text === text ? { next: start + 1, streaming: false } : -1;

  // Ответ агента ещё печатается: на экране — начало куска. Точное совпадение
  // сюда не попадает — оно ниже, как обычное.
  if (isLastOnScreen && chunk.text !== text && chunk.text.startsWith(text)) {
    return { next: start + 1, streaming: true };
  }

  let rest = text;
  let k = start;
  while (rest && k < chunks.length && chunks[k].role === 'assistant') {
    const piece = chunks[k].text;
    if (!rest.startsWith(piece)) break;
    const after = rest.slice(piece.length);
    // Кусок кончается на границе слова, а не посреди него.
    if (after && !/^\s/.test(after)) break;
    rest = after.trim();
    k += 1;
  }
  if (!rest) return k > start ? { next: k, streaming: false } : -1;
  // Checkpoint кончился раньше, чем собрался ответ: он сохранён не весь.
  return k === chunks.length && k > start ? { next: k, streaming: false } : -1;
};

/** Сколько сообщений экрана сошлось подряд с `start` и где остановился checkpoint. */
const alignFrom = (screen, chunks, start) => {
  let k = start;
  let matched = 0;
  for (let i = 0; i < screen.length; i += 1) {
    if (k === chunks.length) return { matched, end: k, complete: true, streaming: false };
    const step = consumeMessage(screen[i], chunks, k, i === screen.length - 1);
    if (step === -1) return { matched, end: k, complete: false, streaming: false };
    matched += 1;
    k = step.next;
    if (step.streaming) return { matched, end: k, complete: true, streaming: true };
  }
  return { matched, end: k, complete: true, streaming: false };
};

const sharesAnyMessage = (screen, chunks) =>
  screen.some((m) => {
    const text = normalize(m.text);
    return chunks.some(
      (c) => c.role === m.role && (c.text === text || c.text.startsWith(text) || text.startsWith(c.text))
    );
  });

/**
 * @param {Array<{role: string, text: string}>} current - сообщения на экране
 * @param {Array<{role: string, text: string}>} checkpoint - история из checkpoint
 * @returns {Array} сообщения на экране, дополненные тем, чего на экране нет
 */
export function mergeCheckpointMessages(current, checkpoint) {
  if (!checkpoint?.length) return current;
  if (!current?.length) return checkpoint;

  const chunks = checkpoint
    .filter(isAlignable)
    .map((m) => ({ message: m, role: m.role, text: normalize(m.text) }));
  if (chunks.length === 0) return current;

  const screen = current.filter(isAlignable);
  if (screen.length === 0) return [...chunks.map((c) => c.message), ...current];

  // Больше совпавших — лучше; при равенстве полное выравнивание лучше
  // оборванного, а из равных — более позднее (цикл идёт с конца).
  let best = null;
  for (let start = chunks.length - 1; start >= 0; start -= 1) {
    const result = alignFrom(screen, chunks, start);
    if (result.matched === 0) continue;
    if (
      !best ||
      result.matched > best.matched ||
      (result.matched === best.matched && result.complete && !best.complete)
    ) {
      best = { start, ...result };
    }
  }

  if (!best) {
    return sharesAnyMessage(screen, chunks) ? current : [...chunks.map((c) => c.message), ...current];
  }
  // Сошлось не всё (текст на экране отличается от checkpoint — например,
  // guardrail переписал показанный ответ): не угадываем, экран как есть.
  // Иначе при повторах в начале экрана подгружалось бы то, что уже на нём
  // (ревью !1296, проход 3).
  if (!best.complete) return current;

  const before = chunks.slice(0, best.start).map((c) => c.message);
  // Хвост checkpoint добавляем, только если весь экран сошёлся: иначе неясно,
  // что из хвоста уже показано.
  // И не добавляем, пока последний ответ на экране ещё печатается: его
  // продолжение приедет в тот же пузырь, и кусок из хвоста задвоился бы.
  // Флаг `isStreaming` на последнем сообщении — тот же случай, даже если его
  // текст уже целиком равен готовому куску (агент между вызовами инструментов).
  const lastStreaming = Boolean(current[current.length - 1]?.isStreaming);
  const after =
    !best.streaming && !lastStreaming && best.matched === screen.length
      ? chunks.slice(best.end).map((c) => c.message)
      : [];
  if (before.length === 0 && after.length === 0) return current;
  return [...before, ...current, ...after];
}
